// Live Audit log. GET /api/v1/audit[?client_id=].
// Client users see only their own events; managers see anything scoped to
// the target client.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageHeader, Pill } from '../../components/shell';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePolling } from '../../lib/usePolling';

const AUDIT_POLL_MS = 60_000;

interface AuditEvent {
  id: string;
  occurred_at: string;
  actor_type: 'user' | 'system';
  actor_email: string | null;
  action: string;
  target_type: string;
  target_id: string;
  ip_address: string | null;
  metadata: Record<string, unknown>;
}

interface AuditPayload {
  client_id: string;
  events: AuditEvent[];
}

export default function Audit() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const clientId = params.get('client_id');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [actorFilter, setActorFilter] = useState<'all' | 'user' | 'system'>('all');

  useEffect(() => {
    if (user && user.role !== 'client' && !clientId) {
      navigate('/fund', { replace: true });
    }
  }, [user, clientId, navigate]);

  const url = '/audit/' + (clientId ? `?client_id=${clientId}` : '');
  const pollingEnabled = !!user && (user.role === 'client' || !!clientId);
  const { data, error } = usePolling<AuditPayload>(
    (signal) => api<AuditPayload>(url, { signal }),
    { intervalMs: AUDIT_POLL_MS, deps: [url], enabled: pollingEnabled },
  );

  useEffect(() => {
    if (!error) { setErrorMsg(null); return; }
    const status = (error as { status?: number })?.status;
    if (status === 404 && user?.role !== 'client') { navigate('/fund', { replace: true }); return; }
    setErrorMsg(status === 409 ? 'Your portfolio is being set up. Transactions and reports will appear here once trading begins.' : 'Failed to load audit log.');
  }, [error, user, navigate]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.events.filter(e => actorFilter === 'all' || e.actor_type === actorFilter);
  }, [data, actorFilter]);

  if (errorMsg) return <div className="view container"><p className="serif-body" style={{ color: 'var(--accent)' }}>{errorMsg}</p></div>;
  if (!data) return (
    <div className="view container">
      <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
                  textTransform: 'uppercase', color: 'var(--ink-3)' }}>Loading…</p>
    </div>
  );

  return (
    <div className="view container">
      <PageHeader
        kicker="AUDIT LOG"
        title={<><em>Activity</em> record.</>}
        meta={<span className="label-s">READ-ONLY · {filtered.length} EVENTS</span>}
      />

      <div className="filter-bar">
        <span className="label" style={{ marginRight: 12 }}>ACTOR</span>
        <div className="filter-group">
          {(['all', 'user', 'system'] as const).map(a => (
            <button key={a} className="filter-chip"
                    data-active={actorFilter === a ? '1' : '0'}
                    onClick={() => setActorFilter(a)}>
              {a.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <section style={{ marginTop: 32 }}>
        {filtered.length === 0 ? (
          <p className="serif-body" style={{ color: 'var(--ink-2)' }}>No events recorded yet.</p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 220 }}>TIMESTAMP</th>
                <th style={{ width: 100 }}>ACTOR</th>
                <th>ACTION</th>
                <th>DETAILS</th>
                <th style={{ width: 140 }}>IP</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id}>
                  <td className="num" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                    {new Date(e.occurred_at).toISOString().slice(0, 19).replace('T', ' ')} UTC
                  </td>
                  <td>
                    <Pill tone={e.actor_type === 'system' ? '' : 'ink'}>
                      {e.actor_type === 'system' ? 'SYSTEM' : (e.actor_email || 'USER')}
                    </Pill>
                  </td>
                  <td><span className="serif" style={{ fontSize: 15 }}>{e.action}</span></td>
                  <td style={{ color: 'var(--ink-2)', fontSize: 14 }}>
                    {e.target_type ? `${e.target_type}:${e.target_id?.slice(0, 8) || '—'}` : '—'}
                  </td>
                  <td className="num" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{e.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={{ marginTop: 64 }}>
        <div style={{ maxWidth: '76ch' }}>
          <div className="label" style={{ marginBottom: 12 }}>NOTE</div>
          <p className="serif-body" style={{ margin: 0, fontSize: 16 }}>
            This log records every authentication, every trade executed on your behalf, and every system reconciliation. It is append-only and cannot be edited.
          </p>
        </div>
      </section>
    </div>
  );
}
